import {
  Instacart_Catalog_Catflow_V1_StepCategory,
  type Instacart_Catalog_Catflow_V1_StepVersion,
} from '../api'

type ExtractionModelProperty = {
  propertyName: string
  required: boolean
  images: boolean
}

export enum AttributeManagementCategoryEnum {
  ExtractionModel = 'EXTRACTION_MODEL',
  Unknown = 'UNKNOWN',
  Input = 'INPUT',
  Pipeline = 'PIPELINE',
  Audit = 'AUDIT',
  DataManipulation = 'DATA_MANIPULATION',
  Publish = 'PUBLISH',
  APIService = 'API_SERVICE',
  Control = 'CONTROL',
}

export enum AttributeManagementNextStepTypeEnum {
  Halt = 'halt',
  NumericRange = 'numericRange',
  FieldMatch = 'fieldMatch',
  RegexRules = 'regexRules',
  Fork = 'fork',
  SingleNextStep = 'singleNextStep',
}

export enum AttributeManagementTypeEnum {
  LLMPromptTemplate = 'LLMPromptTemplate',
  SnowflakeQueryInput = 'SnowflakeQueryInput',
  Pipeline = 'Pipeline',
  QAS = 'QAS',
  Regex = 'Regex',
  Publish = 'Publish',
  PublishToSnowflake = 'PublishToSnowflake',
  DataBridge = 'DataBridge',
  Control = 'Control',
}

export const TypesForCategory: Record<
  AttributeManagementCategoryEnum,
  AttributeManagementTypeEnum[]
> = {
  [AttributeManagementCategoryEnum.ExtractionModel]: [
    AttributeManagementTypeEnum.LLMPromptTemplate,
  ],
  [AttributeManagementCategoryEnum.Control]: [],
  [AttributeManagementCategoryEnum.Unknown]: [],
  [AttributeManagementCategoryEnum.Input]: [AttributeManagementTypeEnum.SnowflakeQueryInput],
  [AttributeManagementCategoryEnum.Pipeline]: [AttributeManagementTypeEnum.Pipeline],
  [AttributeManagementCategoryEnum.Audit]: [AttributeManagementTypeEnum.QAS],
  [AttributeManagementCategoryEnum.DataManipulation]: [AttributeManagementTypeEnum.Regex],
  [AttributeManagementCategoryEnum.Publish]: [
    AttributeManagementTypeEnum.Publish,
    AttributeManagementTypeEnum.PublishToSnowflake,
  ],
  [AttributeManagementCategoryEnum.APIService]: [AttributeManagementTypeEnum.DataBridge],
}

const COMMON_DEFAULT_STEP = {
  id: '',
  name: '',
  description: '',
  owner: '',
}

const COMMON_DEFAULT_STEP_VERSION = {
  id: '',
  createdWhy: '',
  createdAt: '',
  version: '',
  isLive: false,
  state: 'testing',
}

export const DefaultStep: Record<AttributeManagementTypeEnum, AttributeManagementStep> = {
  [AttributeManagementTypeEnum.Control]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Control,
    type: AttributeManagementTypeEnum.Control,
    versions: [],
  },
  [AttributeManagementTypeEnum.LLMPromptTemplate]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.ExtractionModel,
    type: AttributeManagementTypeEnum.LLMPromptTemplate,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          model: '',
          prompt: '',
          properties: [],
          usePrefix: false,
          appendTaskData: true,
          outputPrefix: '',
          productionOperationModel: 'sync',
          testOperationModel: 'sync',
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.SnowflakeQueryInput]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Input,
    type: AttributeManagementTypeEnum.SnowflakeQueryInput,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          query: '',
          limit: 50000,
          rankingField: '',
          useRanking: false,
          useDeduplication: false,
          deduplicationFields: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.Pipeline]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Pipeline,
    type: AttributeManagementTypeEnum.Pipeline,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          enableSchedule: false,
          hoursInterval: 24,
          steps: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.QAS]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Audit,
    type: AttributeManagementTypeEnum.QAS,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          qasWorkflow: '',
          reuse: false,
          reuseSince: 1,
          components: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.Regex]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.DataManipulation,
    type: AttributeManagementTypeEnum.Regex,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          mutations: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.Publish]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Publish,
    type: AttributeManagementTypeEnum.Publish,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          productIdField: '',
          useRetailerId: false,
          useLocale: false,
          retailerIdField: '',
          localeField: '',
          data: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.PublishToSnowflake]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.Publish,
    type: AttributeManagementTypeEnum.PublishToSnowflake,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          snowflakeTable: '',
          snowflakeBatchSize: 1000,
          columnMappings: [],
        },
        stepId: '',
      },
    ],
  },
  [AttributeManagementTypeEnum.DataBridge]: {
    ...COMMON_DEFAULT_STEP,
    category: AttributeManagementCategoryEnum.APIService,
    type: AttributeManagementTypeEnum.DataBridge,
    versions: [
      {
        ...COMMON_DEFAULT_STEP_VERSION,
        attributes: {
          catalogDataBridgeName: '',
        },
        stepId: '',
      },
    ],
  },
}

export const AttributeManagementCategoryEnumToGraphQLEnum: Record<
  AttributeManagementCategoryEnum,
  Instacart_Catalog_Catflow_V1_StepCategory
> = {
  [AttributeManagementCategoryEnum.ExtractionModel]:
    Instacart_Catalog_Catflow_V1_StepCategory.ExtractionModel,
  [AttributeManagementCategoryEnum.Control]: Instacart_Catalog_Catflow_V1_StepCategory.Unknown,
  [AttributeManagementCategoryEnum.Unknown]: Instacart_Catalog_Catflow_V1_StepCategory.Unknown,
  [AttributeManagementCategoryEnum.Input]: Instacart_Catalog_Catflow_V1_StepCategory.Input,
  [AttributeManagementCategoryEnum.Pipeline]: Instacart_Catalog_Catflow_V1_StepCategory.Pipeline,
  [AttributeManagementCategoryEnum.Audit]: Instacart_Catalog_Catflow_V1_StepCategory.Audit,
  [AttributeManagementCategoryEnum.DataManipulation]:
    Instacart_Catalog_Catflow_V1_StepCategory.Normalization,
  [AttributeManagementCategoryEnum.Publish]: Instacart_Catalog_Catflow_V1_StepCategory.Publish,
  [AttributeManagementCategoryEnum.APIService]:
    Instacart_Catalog_Catflow_V1_StepCategory.ApiService,
}

export const GraphQLEnumToAttributeManagementCategoryEnum: Record<
  Instacart_Catalog_Catflow_V1_StepCategory,
  AttributeManagementCategoryEnum
> = {
  [Instacart_Catalog_Catflow_V1_StepCategory.ExtractionModel]:
    AttributeManagementCategoryEnum.ExtractionModel,
  [Instacart_Catalog_Catflow_V1_StepCategory.Input]: AttributeManagementCategoryEnum.Input,
  [Instacart_Catalog_Catflow_V1_StepCategory.Unknown]: AttributeManagementCategoryEnum.Unknown,
  [Instacart_Catalog_Catflow_V1_StepCategory.Pipeline]: AttributeManagementCategoryEnum.Pipeline,
  [Instacart_Catalog_Catflow_V1_StepCategory.Audit]: AttributeManagementCategoryEnum.Audit,
  [Instacart_Catalog_Catflow_V1_StepCategory.Normalization]:
    AttributeManagementCategoryEnum.DataManipulation,
  [Instacart_Catalog_Catflow_V1_StepCategory.Validation]: AttributeManagementCategoryEnum.Unknown,
  [Instacart_Catalog_Catflow_V1_StepCategory.Publish]: AttributeManagementCategoryEnum.Publish,
  [Instacart_Catalog_Catflow_V1_StepCategory.ApiService]:
    AttributeManagementCategoryEnum.APIService,
}

export const graphQLStepVersionToAttributeManagementStepVersion = (
  stepVersion: Instacart_Catalog_Catflow_V1_StepVersion
): AttributeManagementStepVersion => ({
  attributes: stepVersion.attributes as any,
  createdWhy: stepVersion.createdWhy || '',
  createdAt: stepVersion.createdAt || '',
  version: stepVersion.version || '',
  id: stepVersion.id || '',
  isLive: false,
  state: stepVersion.state || '',
  stepId: stepVersion.stepId || '',
})

export type AttributeManagementStepVersion = {
  createdWhy: string
  createdAt: string
  version: string
  id: string
  stepId: string
  isLive: boolean
  state: string
  attributes: any
}

export type AttributeManagementStep = {
  id: string | undefined
  name: string
  category: AttributeManagementCategoryEnum
  type: AttributeManagementTypeEnum
  description: string
  owner: string
  versions: AttributeManagementStepVersion[]
}

export type LLMPromptTemplateAttributes = {
  model: string
  prompt: string
  properties: ExtractionModelProperty[]
}

export type SnowflakeQueryInputAttributes = {
  query: string
  rankingField: string
  useRanking: boolean
  useDeduplication: boolean
  deduplicationFields: string[]
  limit: number
}

export type PipelineStepNextStepsRule = {
  // General Attributes
  type: AttributeManagementNextStepTypeEnum
  possibleNextSteps: string[]
  nextPipelineSteps?: string[]
  nextStep?: string

  // Regex steps
  campField?: string
  matches?: { regex: string; nextPipelineStep: string }[]
  noMatchNextPipelineStep?: string

  // Numeric Ranges
  ranges?: { rangeLow: number; rangeHigh: number; nextPipelineStep: string }[]
  defaultNextPipelineStep?: string
  valueField?: string

  // Field Matches
  countRequired?: number
  campFields?: string[]
  nextPipelineStepOnMatchHit?: string
  nextPipelineStepOnMatchMiss?: string
  winningValueOutputField?: string
}

export enum PipelineStepType {
  Task = 'task',
  Control = 'control',
}

export type PipelineStep = {
  type?: string | AttributeManagementTypeEnum | null | undefined
  stepId?: string
  stepVersionId?: string
  version?: string | null
  pipelineStepId?: string
  attributeOverrides?: Record<string, any>
  nextStepsRule?: PipelineStepNextStepsRule | null
}

export type PipelineAttributes = {
  enableSchedule: boolean
  hoursInterval: number
  steps: PipelineStep[]
}
